const EXTENSION_SEPARATOR = '.';
const NOT_FOUND_INDEX = -1;
/** What an extension may be before it reaches the key the server mints: 1–8 letters or digits. */
const STORED_EXTENSION_PATTERN = /^[a-z0-9]{1,8}$/i;

/**
 * The extension an upload intent declares — the file's own, or the caller's fallback: the EFF-26320 spec's
 * **SEC-2**, the Stored extension.
 *
 * **Not a port of brain.** Moved unchanged from `domain-clarity-chat-core`'s
 * `clarity-chat-attachment-upload.utils.ts` (`resolveClarityChatStoredExtension`), so the chat and the Domain files
 * panel declare one extension for one file name. It replaces brain's `file.name.split('.').pop() ?? 'bin'`
 * (`apps/web/src/shared/api/domainFileStorageService.ts:140`, commit `9015ce88c`). The chat's two libraries import it
 * from here, each passing its own fallback.
 *
 * **Only a short alphanumeric tail after the last dot counts as an extension.** Clarity's server interpolates `ext`
 * into the object key it mints without sanitising it, so a tail carrying `/` or `..` would give the client a say in
 * where the object lands. A name with no dot, or a tail outside the pattern, takes the fallback instead — which
 * changes the stored extension, the object key and the durable URL of such a name against brain's. It constrains
 * the keys this client requests and establishes nothing server-side.
 *
 * **The answer is part of a file name a person typed.** It reaches the stored object's key and must never be
 * logged.
 *
 * @param fileName The reader's file name.
 * @param fallbackExtension What to use when the name carries nothing usable.
 * @returns The extension to declare, without a dot.
 */
export function resolveClarityStoredExtension(fileName: string, fallbackExtension: string): string {
	const separatorIndex = fileName.lastIndexOf(EXTENSION_SEPARATOR);

	if (separatorIndex === NOT_FOUND_INDEX) {
		return fallbackExtension;
	}

	const extension = fileName.slice(separatorIndex + 1);

	return STORED_EXTENSION_PATTERN.test(extension) ? extension : fallbackExtension;
}
